import { useCallback, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import type {
  BuyerImportListingInterestDto,
  BuyerSmartImportAnalysisDto,
  BuyerSmartImportReviewStateDto,
  BuyerSmartImportSource,
} from "@/api/buyerSmartImport";
import type { BuyerLeadSource } from "@/api/buyers";
import { PageHeader } from "@/components/layout/PageHeader";
import {
  smartImportErrorMessage,
  useAnalyzeSmartImport,
  useCommitSmartImport,
  useDebounced,
  useSmartImportBatch,
  useSmartImportBatches,
  useSmartImportInterests,
  useSmartImportPreview,
  useUndoSmartImport,
} from "@/hooks/buyers/useSmartImport";
import { SmartImportHistory } from "./smartImport/SmartImportHistory";
import { SmartImportProgress } from "./smartImport/SmartImportProgress";
import { SmartImportReview } from "./smartImport/SmartImportReview";
import { SmartImportUpload } from "./smartImport/SmartImportUpload";
import { smartImportCopy } from "./smartImport/smartImportCopy";
import {
  EMPTY_PLACEMENT,
  listingSignature,
  reviewFromAnalysis,
  reviewSignature,
  withSourceOnEveryTab,
  type SmartImportPlacement,
} from "./smartImport/smartImportReview";

/**
 * Bulk buyer intake — the third way a buyer enters this business, alongside the
 * prequalification funnel and manual entry.
 *
 * <p>🔴 <b>Same route, same name, replaced flow (D14).</b> The CSV upload this
 * page used to be asked the operator to reshape their file to Phoenix's column
 * names and then told them what had happened afterwards. This reads whatever
 * they have, proposes a mapping, and shows them what it will create BEFORE
 * anything is written — so the one irreversible moment is the one they
 * approved. The pieces are deliberately reusable: `/leads/import` can adopt
 * them without this page moving, and nothing here touches `LeadImportService`
 * or that route.</p>
 *
 * <p>🔴 <b>The uploaded file never leaves this component.</b> It is held in
 * state and re-sent on every request; nothing about it is stored on the server
 * between them (D5), which is why `analyze`, `preview` and `commit` all carry
 * it rather than a handle to it.</p>
 *
 * <p>The dry-run is a QUERY, debounced on the review signature — so flipping a
 * choice back re-serves the dry-run it replaced rather than paying for it
 * twice, and a sentinel list typed a character at a time is one round-trip
 * rather than fifteen.</p>
 */

/** How long a review change settles before the dry-run re-runs. */
export const SMART_IMPORT_REVIEW_DEBOUNCE_MS = 400;

export function BuyerImportPage() {
  // --- the source, which is never persisted anywhere ------------------------
  const [file, setFile] = useState<File | null>(null);
  const [pastedText, setPastedText] = useState("");
  const [placement, setPlacement] = useState<SmartImportPlacement>(EMPTY_PLACEMENT);
  /*
    🔴 HELD, not applied and forgotten. The pickers live on the LANDING, so at
    the moment an advertisement is chosen there is no review state to write a
    Source into — which is every ordinary import, because choosing the
    advertisement before reading the file is the order the screen invites. The
    refill below would then early-return on a null review and D16's promise
    (stated in the picker's own helper text, on that same screen) would be
    kept only for an operator who happened to go back and change the pick
    afterwards.
  */
  const [advertisementSource, setAdvertisementSource] = useState<BuyerLeadSource | null>(null);

  // --- what the file turned out to be --------------------------------------
  const [analysis, setAnalysis] = useState<BuyerSmartImportAnalysisDto | null>(null);
  const [review, setReview] = useState<BuyerSmartImportReviewStateDto | null>(null);

  // --- the queued job -------------------------------------------------------
  const [batchId, setBatchId] = useState<string | null>(null);
  const [undoingBatchId, setUndoingBatchId] = useState<string | null>(null);

  const source: BuyerSmartImportSource = useMemo(
    () => ({ file, pastedText: file ? null : pastedText }),
    [file, pastedText],
  );

  const analyze = useAnalyzeSmartImport();
  const commit = useCommitSmartImport();
  const undo = useUndoSmartImport();
  const batches = useSmartImportBatches();
  const batch = useSmartImportBatch(batchId);

  const signature = review ? reviewSignature(review) : "";
  const settledSignature = useDebounced(signature, SMART_IMPORT_REVIEW_DEBOUNCE_MS);
  /*
    The query is keyed on the SETTLED signature but sends the LIVE review, and
    the two are the same object whenever the debounce has caught up. Gating on
    equality is what stops a mid-typing render firing a request for a key that
    describes a state the operator has already moved past.
  */
  const reviewSettled = signature === settledSignature;

  const listings = review ? listingSignature(review) : "";
  const settledListings = useDebounced(listings, SMART_IMPORT_REVIEW_DEBOUNCE_MS);

  const preview = useSmartImportPreview(
    source,
    analysis?.contentHash ?? null,
    review,
    settledSignature,
    analysis != null && reviewSettled && batchId == null,
  );

  const interests = useSmartImportInterests(
    source,
    analysis?.contentHash ?? null,
    review,
    settledListings,
    analysis != null &&
      (review?.inferInterests ?? false) &&
      listings === settledListings &&
      batchId == null,
  );

  /*
    The interests the operator has edited win over the ones the model returned;
    until they touch one there is nothing of theirs to keep, so the answer is
    whatever the call last said. Held on the review state because that is what
    the commit sends — an interests table the server never sees is a table
    nobody acted on.
  */
  const interestRows: readonly BuyerImportListingInterestDto[] =
    review?.interests.length ? review.interests : (interests.data?.listings ?? []);

  const runAnalyze = useCallback(
    (forceAi: boolean) => {
      analyze.mutate(
        { source, forceAi },
        {
          onSuccess: (next) => {
            setAnalysis(next);
            const built = reviewFromAnalysis(next, placement);
            // An advertisement chosen on the landing sets the Source here,
            // where the tabs first exist.
            setReview(
              advertisementSource ? withSourceOnEveryTab(built, advertisementSource) : built,
            );
          },
        },
      );
    },
    [analyze, source, placement, advertisementSource],
  );

  /** Put every tab's Source on the advertisement's channel (D16). */
  const applyAdvertisementSource = useCallback((next: BuyerLeadSource | null) => {
    // Remembered whichever stage it arrives at — including the cleared case,
    // so a withdrawn advertisement does not go on setting a Source on the
    // next file the operator reads.
    setAdvertisementSource(next);
    if (!next) return;
    setReview((current) => (current ? withSourceOnEveryTab(current, next) : current));
  }, []);

  const changePlacement = useCallback((next: SmartImportPlacement) => {
    setPlacement(next);
    // The placement is part of the review state the server hashes, so a change
    // made after the file was read has to reach it or the commit would carry
    // the placement the analysis started with.
    setReview((current) =>
      current
        ? {
            ...current,
            buyerTeamId: next.buyerTeamId,
            assignToSpecialistUserId: next.assignToSpecialistUserId,
            advertisementId: next.advertisementId,
          }
        : current,
    );
  }, []);

  const startOver = useCallback(() => {
    setFile(null);
    setPastedText("");
    setAnalysis(null);
    setReview(null);
    setBatchId(null);
    /*
      🔴 The remembered Source is NOT cleared here, and it is not an omission.
      `startOver` deliberately keeps the PLACEMENT — importing several files to
      one team is the reason to press it — so the advertisement is still picked
      and its channel is still what the picker's own helper text promises the
      next file's tabs will carry. Clearing one half of a pair the two writers
      in `SmartImportPickers` always move together (`pickAdvertisement` sets
      both; `pickTeam` clears both when the new team cannot keep the ad) is how
      the screen comes to contradict itself: the advertisement on screen, the
      Source silently no longer following it.
    */
    analyze.reset();
    commit.reset();
  }, [analyze, commit]);

  const stage: "upload" | "review" | "progress" =
    batchId != null ? "progress" : analysis && review ? "review" : "upload";

  return (
    <>
      <Link
        to="/buyers"
        className="inline-block mb-space-2 text-caption text-gold-shadow hover:underline"
      >
        ← Buyers
      </Link>
      <PageHeader
        eyebrow="Buyers · Import"
        title={smartImportCopy.uploadTitle}
        description={smartImportCopy.uploadDescription}
      />

      <div className="grid gap-space-6">
        {stage === "upload" && (
          <SmartImportUpload
            file={file}
            onFile={setFile}
            pastedText={pastedText}
            onPastedText={setPastedText}
            placement={placement}
            onPlacement={changePlacement}
            onAdvertisementSource={applyAdvertisementSource}
            onAnalyze={() => runAnalyze(false)}
            analyzing={analyze.isPending}
            error={analyze.isError ? smartImportErrorMessage(analyze.error) : null}
          />
        )}

        {stage === "review" && analysis && review && (
          <SmartImportReview
            analysis={analysis}
            review={review}
            onReview={setReview}
            preview={preview.data ?? null}
            previewLoading={preview.isFetching || !reviewSettled}
            previewError={preview.isError ? smartImportErrorMessage(preview.error) : null}
            interests={interestRows}
            interestsLoading={interests.isFetching}
            interestsFailureMessage={
              interests.isError
                ? smartImportErrorMessage(interests.error)
                : (interests.data?.aiFailureMessage ?? null)
            }
            onInterests={(next) => setReview((c) => (c ? { ...c, interests: next } : c))}
            onRemapWithAi={() => runAnalyze(true)}
            remapping={analyze.isPending}
            committing={commit.isPending}
            commitError={commit.isError ? smartImportErrorMessage(commit.error) : null}
            onCommit={() => {
              if (!preview.data) return;
              commit.mutate(
                {
                  source,
                  review,
                  contentHash: preview.data.contentHash,
                  planHash: preview.data.planHash,
                },
                { onSuccess: (queued) => setBatchId(queued.id) },
              );
            }}
          />
        )}

        {stage === "progress" && batch.data && (
          <SmartImportProgress batch={batch.data} onStartOver={startOver} />
        )}

        <SmartImportHistory
          batches={batches.data ?? []}
          /*
            `isLoading`, not `isPending`: this is a QUERY, and the two differ
            on a background refetch — `isPending` is true whenever there is no
            data, but `isFetching` alone would blank a history the operator is
            already reading back to "Loading…" every time it revalidates. It is
            also what `pendingSpinnersHaveAMutation` is asking about: an
            `isPending` spinner names a MUTATION, and this file fires none.
          */
          loading={batches.isLoading}
          onUndo={(id) => {
            setUndoingBatchId(id);
            undo.mutate(id);
          }}
          undoing={undo.isPending}
          undoingBatchId={undoingBatchId}
          undoResult={undo.data ?? null}
          undoError={undo.isError ? smartImportErrorMessage(undo.error) : null}
        />
      </div>
    </>
  );
}
